import { API_BASE_URL } from "../../services/api";
import { getAssistantImagesForDisplay } from "../../utils/chatLogSource";

export type WorkerChatMessage = {
    id: string;
    messageId?: string;
    role: "user" | "assistant";
    question?: string;
    answer?: string;
    images?: string[];
    timestamp: string;
    sopSource?: {
        sopName: string;
        sectionTitle: string;
        fileId: string;
        documentContent?: string;
    };
    sourcedFrom?: {
        documentName?: string;
        sectionName?: string;
    };
    pdfUrl?: string;
};

export function chatMessageDedupeKey(message: WorkerChatMessage): string {
    if (message.messageId) {
        return message.messageId;
    }
    const body = message.role === "user" ? (message.question ?? "") : (message.answer ?? "");
    return `${message.role}|${message.timestamp}|${body}`;
}

/** Collapse duplicate rows (same messageId) from double-append or cache/server merge. */
export function dedupeChatMessages(messages: WorkerChatMessage[]): WorkerChatMessage[] {
    const seen = new Set<string>();
    const result: WorkerChatMessage[] = [];
    for (const message of messages) {
        const key = chatMessageDedupeKey(message);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(message);
    }
    return result;
}

export function mergeChatMessages(
    local: WorkerChatMessage[],
    remote: WorkerChatMessage[],
): WorkerChatMessage[] {
    return dedupeChatMessages([...local, ...remote]).sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
}

export function mapServerMessagesToChat(messages: unknown[]): WorkerChatMessage[] {
    const mapped = (messages || []).map((raw: unknown, idx: number) => {
        const m = raw as Record<string, unknown>;
        const pdfUrlRaw = m.pdfUrl as string | undefined;
        const sopSource = m.sopSource as WorkerChatMessage["sopSource"] | undefined;
        const computedPdfUrl = pdfUrlRaw
            ? pdfUrlRaw.startsWith("http")
                ? pdfUrlRaw
                : `${API_BASE_URL}${pdfUrlRaw.startsWith("/") ? pdfUrlRaw : `/${pdfUrlRaw}`}`
            : sopSource?.fileId
              ? `${API_BASE_URL}/documents/${sopSource.fileId}/pdf`
              : undefined;

        let fallbackSourcedFrom: WorkerChatMessage["sourcedFrom"];
        if (!sopSource && !m.sourcedFrom && m.role === "assistant" && typeof m.text === "string") {
            const sourcedFromMatch = m.text.match(/Sourced from:?\s*([^—\n]+)/i);
            const sectionMatch = m.text.match(/—\s*([^—\n]+)/i);
            if (sourcedFromMatch) {
                fallbackSourcedFrom = {
                    documentName: sourcedFromMatch[1].trim(),
                    sectionName: sectionMatch ? sectionMatch[1].trim() : undefined,
                };
            }
        }

        const role = m.role as "user" | "assistant";
        const text = typeof m.text === "string" ? m.text : "";

        return {
            id: (m.messageId as string) || `${Date.now()}-${idx}`,
            messageId: (m.messageId as string) || undefined,
            role,
            question: role === "user" ? text : undefined,
            answer: role === "assistant" ? text : undefined,
            images: getAssistantImagesForDisplay(
                text,
                (m.images as string[]) || [],
            ),
            timestamp: (m.timestamp as string) || new Date().toISOString(),
            sopSource: sopSource || undefined,
            sourcedFrom: (m.sourcedFrom as WorkerChatMessage["sourcedFrom"]) || fallbackSourcedFrom,
            pdfUrl: computedPdfUrl,
        };
    });
    return dedupeChatMessages(mapped);
}
